import {
  AccountsService,
  AccountType,
  MeResponse,
} from 'base/accounts_service';
import { AccountStore } from 'base/account_store';
import { Asset } from 'base/asset';
import { delay } from 'base/helpers';
import { Avatar } from 'base/ui/avatar';
import { Banner } from 'base/ui/banner';
import { Button } from 'base/ui/button';
import { Checkbox } from 'base/ui/checkbox';
import { Dialog } from 'base/ui/dialog';
import { InvisibleForm, RedErrorText, useSubmit } from 'base/ui/form';
import { IconGoogle } from 'base/ui/icons';
import { FormInput } from 'base/ui/input';
import { Inline, Rows, SpacerGrow } from 'base/ui/layout';
import { DividerLine } from 'base/ui/panel';
import { ToastQueue } from 'base/ui/toast';
import { COLOR_TOKENS } from 'base/ui/tokens';
import {
  TextRegularLight,
  TextSmall,
  TextSmallLight,
  TextTinyLight,
  Title1,
  Title6,
} from 'base/ui/typography';
import { CodeEntryForm } from 'blocks/auth/forms';
import { observer } from 'mobx-react-lite';
import React from 'react';
import styles from './account_form.css';
import { TwoColumn, useAvatarUpload } from './ui';

export function AccountForm({
  accountStore,
  toastQueue,
}: {
  accountStore: AccountStore;
  toastQueue: ToastQueue;
}) {
  const [value, setValue] = React.useState<MeResponse | undefined>(undefined);
  const [error, setError] = React.useState<unknown | undefined>(undefined);

  const accountsService = accountStore.service;

  React.useEffect(() => {
    accountsService.me().then(setValue).catch(setError);
  }, [accountsService]);

  const fufilledOrLoadingForm = (
    <>
      {value ? (
        <ProfileForm
          existingFirstName={value.firstName}
          existingLastName={value.lastName}
          currentAvatar={value.avatar}
          color={value.color}
          accountsService={accountsService}
          toastQueue={toastQueue}
        />
      ) : (
        <ProfileFormView
          disabled={true}
          firstName=""
          lastName=""
          avatarField={
            <Avatar size="xxlarge" color={COLOR_TOKENS.colorLightGrey3} />
          }
        />
      )}

      <DividerLine />
      <ChangeEmailForm
        disabled={value?.accountType === 'google'}
        email={value?.email || ''}
        accountsService={accountsService}
        toastQueue={toastQueue}
        accountType={value?.accountType}
      />

      <DividerLine />
      <ChangePasswordForm
        disabled={value?.accountType === 'google'}
        accountsService={accountsService}
        toastQueue={toastQueue}
      />

      <DividerLine />

      <TrainingDataForm accountStore={accountStore} toastQueue={toastQueue} />
    </>
  );
  return (
    <Rows spacing={32}>
      <Title1>Account</Title1>
      <RedErrorText error={error} />
      {error == null && fufilledOrLoadingForm}
    </Rows>
  );
}

function AvatarField({
  firstName,
  lastName,
  color,
  accountsService,
  toastQueue,
  currentAvatar,
}: {
  firstName: string;
  lastName: string;
  color: string;
  accountsService: AccountsService;
  toastQueue: ToastQueue;
  currentAvatar?: Asset;
}) {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const selectFile = React.useCallback(() => {
    inputRef.current?.click();
  }, []);
  const { avatar, loading, chooseAvatar, deleteAvatar } = useAvatarUpload({
    upload: (file: File) => accountsService.uploadAvatar(file),
    remove: () => accountsService.deleteAvatar(),
    toastQueue,
    currentAvatar,
  });
  return (
    <Inline spacing={16} yalign="center">
      <Avatar
        size="xlarge"
        avatar={avatar}
        firstName={firstName}
        lastName={lastName}
        color={color}
        onClick={selectFile}
      />
      <Rows spacing={8} xalign="start">
        <Inline spacing={16}>
          <input
            ref={inputRef}
            type="file"
            className={styles.hidden}
            tabIndex={-1}
            aria-hidden="true"
            accept="image/jpeg,image/png,image/webp"
            onChange={chooseAvatar}
          />
          <Button
            onClick={selectFile}
            loading={loading}
            type="button"
            label={avatar ? 'Upload new image' : 'Upload image'}
          />
          {avatar && !loading && (
            <Button
              variant="select"
              onClick={deleteAvatar}
              loading={loading}
              type="button"
              label="Delete"
            />
          )}
        </Inline>
        <TextTinyLight color="grey">
          Recommended 160x160px in PNG or JPG format
        </TextTinyLight>
      </Rows>
    </Inline>
  );
}

function ProfileForm({
  existingFirstName,
  existingLastName,
  accountsService,
  toastQueue,
  color,
  currentAvatar,
}: {
  existingFirstName: string;
  existingLastName: string;
  accountsService: AccountsService;
  toastQueue: ToastQueue;
  color: string;
  currentAvatar?: Asset;
}) {
  const [saved, setSaved] = React.useState({
    firstName: existingFirstName,
    lastName: existingLastName,
  });
  const [firstName, setFirstName] = React.useState(existingFirstName);
  const [lastName, setLastName] = React.useState(existingLastName);

  const [loading, error, handleSubmit] = useSubmit(async () => {
    await accountsService.updateAttributes({ firstName, lastName });
    setSaved({ firstName, lastName });
    toastQueue.push({ label: 'Name changed' });
  });

  const avatarField = (
    <AvatarField
      firstName={firstName}
      lastName={lastName}
      currentAvatar={currentAvatar}
      color={color}
      accountsService={accountsService}
      toastQueue={toastQueue}
    />
  );

  return (
    <InvisibleForm onSubmit={handleSubmit} loading={loading}>
      <ProfileFormView
        firstName={firstName}
        lastName={lastName}
        avatarField={avatarField}
        onChangeFirstName={setFirstName}
        onChangeLastName={setLastName}
        loading={loading}
        edited={firstName !== saved.firstName || lastName !== saved.lastName}
      />
      <RedErrorText error={error} />
    </InvisibleForm>
  );
}

function ProfileFormView({
  firstName,
  lastName,
  avatarField,
  onChangeFirstName,
  onChangeLastName,
  disabled,
  loading,
  edited,
  completed,
}: {
  firstName: string;
  lastName: string;
  avatarField: React.ReactNode;
  onChangeFirstName?: (value: string) => void;
  onChangeLastName?: (value: string) => void;
  disabled?: boolean;
  loading?: boolean;
  edited?: boolean;
  completed?: boolean;
}) {
  return (
    <Rows spacing={24}>
      <Inline spacing={8}>
        <Title6>Profile</Title6>
        <SpacerGrow />
        <Button
          type="submit"
          variant="primary"
          label={completed ? 'Saved' : 'Save'}
          loading={loading}
          disabled={disabled || !edited || completed}
          tooltip={!edited ? 'No changes to save' : undefined}
        />
      </Inline>
      {avatarField}
      <TwoColumn>
        <Rows spacing={8}>
          <TextSmall color="grey">First Name</TextSmall>
          <FormInput
            required
            size="large"
            type="text"
            autoComplete="given-name"
            value={firstName}
            onChange={onChangeFirstName}
            disabled={disabled}
          />
        </Rows>
        <Rows spacing={8}>
          <TextSmall color="grey">Last Name</TextSmall>
          <FormInput
            required
            size="large"
            type="text"
            autoComplete="family-name"
            value={lastName}
            onChange={onChangeLastName}
            disabled={disabled}
          />
        </Rows>
      </TwoColumn>
    </Rows>
  );
}

function ChangeEmailForm({
  email,
  accountsService,
  toastQueue,
  disabled,
  accountType,
}: {
  email: string;
  accountsService: AccountsService;
  toastQueue: ToastQueue;
  disabled?: boolean;
  accountType?: AccountType;
}) {
  // new email entered in the text box
  const [newEmail, setNewEmail] = React.useState<string | undefined>();
  // email that needs to finish (be verified)
  const [finishEmail, setFinishEmail] = React.useState<string | undefined>();

  const inputError =
    newEmail == null || newEmail === email
      ? 'That is your current email'
      : !newEmail.includes('@')
      ? 'Please enter a valid email'
      : undefined;

  const [loading, error, handleSubmit] = useSubmit(async () => {
    if (inputError || !newEmail) throw new Error(inputError);
    await accountsService.changeEmailStart({ newEmail });
    setFinishEmail(newEmail);
  });

  // this is dummy now but we will add okta in a couple of weeks and need to support multiple sso providers
  let iconType;
  let loginType;
  switch (accountType) {
    case 'google':
      iconType = IconGoogle;
      loginType = 'Google';
      break;
    default:
      iconType = null;
      loginType = 'Standard';
  }

  return (
    <InvisibleForm
      onSubmit={handleSubmit}
      loading={loading}
      disabled={disabled}
    >
      <Rows spacing={24}>
        {loginType !== 'Standard' && (
          <Banner variant="grey3" icon={iconType}>
            <Inline spacing={4} yalign="center">
              <TextSmall>You signed up with {loginType}.</TextSmall>
              <TextSmallLight>
                Email changes and password logins aren't supported yet.
              </TextSmallLight>
            </Inline>
          </Banner>
        )}
        <Inline spacing={8}>
          <Title6>Email</Title6>
          <SpacerGrow />
          <Button
            type="submit"
            variant="primary"
            label="Save"
            loading={loading}
            disabled={inputError != null || disabled}
            tooltip={inputError}
          />
        </Inline>
        <TwoColumn>
          <Rows spacing={8}>
            <TextSmall color="grey">Email</TextSmall>
            <FormInput
              size="large"
              type="email"
              autoComplete="email"
              value={newEmail ?? email}
              onChange={setNewEmail}
              disabled={disabled}
            />
          </Rows>
        </TwoColumn>
      </Rows>
      <RedErrorText error={error} />
      {finishEmail && (
        <FinishChangeEmailDialog
          newEmail={finishEmail}
          onClose={() => setFinishEmail(undefined)}
          accountsService={accountsService}
          toastQueue={toastQueue}
        />
      )}
    </InvisibleForm>
  );
}

function FinishChangeEmailDialog({
  newEmail,
  onClose,
  accountsService,
  toastQueue,
}: {
  newEmail: string;
  onClose: () => void;
  accountsService: AccountsService;
  toastQueue: ToastQueue;
}) {
  const [submitting, setSubmitting] = React.useState(false);
  const handleVerify = React.useCallback(
    async (code: string) => {
      setSubmitting(true);
      try {
        await accountsService.changeEmailFinish({ newEmail, code });
        toastQueue.push({ label: 'Email changed' });
        await delay(2_000); // so they can see the toast
        window.location.reload();
        await delay(3_000); // so the new page can load
      } finally {
        setSubmitting(false);
      }
    },
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [accountsService, newEmail],
  );
  return (
    <Dialog
      title="Verify your new email"
      onClose={submitting ? undefined : onClose}
      buttons={
        <>
          <Button
            label="Cancel"
            size="large"
            disabled={submitting}
            onClick={onClose}
          />
          <Button
            label="Verify"
            variant="primary"
            size="large"
            // The form auto-submits when 6 digits are entered, so this button
            // is never clickable
            disabled
            tooltip="Enter the 6-digit code to continue"
            loading={submitting}
          />
        </>
      }
    >
      <TextRegularLight>
        We sent you a six digit confirmation code to {newEmail}. Please enter it
        below to confirm your new email address.
      </TextRegularLight>
      <CodeEntryForm handleVerify={handleVerify} />
    </Dialog>
  );
}

function ChangePasswordForm({
  accountsService,
  toastQueue,
  disabled,
}: {
  accountsService: AccountsService;
  toastQueue: ToastQueue;
  disabled?: boolean;
}) {
  const [existingPassword, setExistingPassword] = React.useState('');
  const [newPassword, setNewPassword] = React.useState('');
  const [confirmNewPassword, setConfirmNewPassword] = React.useState('');

  const disabledReason = !existingPassword
    ? 'Please enter your existing password'
    : newPassword.length < 8
    ? 'Your new password must be at least 8 characters'
    : newPassword === existingPassword
    ? 'The new password is the same as the existing password'
    : newPassword !== confirmNewPassword
    ? 'The new password and confirmation do not match'
    : disabled
    ? 'For this type of account you can not change your password'
    : undefined;

  const [loading, error, handleSubmit] = useSubmit(async () => {
    if (disabledReason) throw new Error(disabledReason);
    await accountsService.resetPassword({
      newPassword,
      oldPassword: existingPassword,
    });
    toastQueue.push({ label: 'Password changed' });
    setExistingPassword('');
    setNewPassword('');
    setConfirmNewPassword('');
  });

  return (
    <InvisibleForm
      loading={loading}
      onSubmit={handleSubmit}
      disabled={disabled}
    >
      <Rows spacing={24}>
        <Inline spacing={8}>
          <Title6>Change Password</Title6>
          <SpacerGrow />
          <Button
            tooltip={disabledReason}
            disabled={disabledReason != null || disabled}
            variant="primary"
            label="Update password"
            loading={loading}
            type="submit"
          />
        </Inline>
        <TwoColumn>
          <Rows spacing={16}>
            <Rows spacing={8}>
              <TextSmall color="grey">Existing Password</TextSmall>
              <FormInput
                size="large"
                type="password"
                autoComplete="current-password"
                value={existingPassword}
                onChange={setExistingPassword}
                disabled={disabled}
              />
            </Rows>
            <Rows spacing={8}>
              <TextSmall color="grey">New Password</TextSmall>
              <FormInput
                size="large"
                type="password"
                autoComplete="new-password"
                value={newPassword}
                onChange={setNewPassword}
                disabled={disabled}
              />
            </Rows>
            <Rows spacing={8}>
              <TextSmall color="grey">Confirm New Password</TextSmall>
              <FormInput
                size="large"
                type="password"
                autoComplete="new-password"
                value={confirmNewPassword}
                onChange={setConfirmNewPassword}
                disabled={disabled}
              />
            </Rows>
          </Rows>
        </TwoColumn>
        <RedErrorText error={error} />
      </Rows>
    </InvisibleForm>
  );
}

const TrainingDataForm = observer(function _TrainingDataForm({
  accountStore,
  toastQueue,
}: {
  accountStore: AccountStore;
  toastQueue: ToastQueue;
}) {
  const optIn = accountStore?.member?.member?.attributes['ai-opt-in'] ?? true;

  const handleSubmit = async () => {
    await accountStore.setAIOptIn(!optIn);
    toastQueue.push({ label: 'AI opt-in changed' });
  };

  return (
    <Rows spacing={8}>
      <Checkbox
        value={optIn}
        onChange={handleSubmit}
        label="Let Relume use your content to train AI models. Steps are taken to protect your privacy and de-identify your data."
      />
    </Rows>
  );
});
